import { Component, AfterViewInit, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions, Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { FunctionService } from '../../services/function.service';
import { ClientsBrands } from '../../services/shipment-management.service';
import {
  DamageManagementService,
  DamageGraphicsResponse,
  DamageReport,
  DamageReportFlow
} from '../../services/damage-management.service';
import { DamageNavBoxComponent } from './damage-overview/damage-nav-box/damage-nav-box.component';

/** Flussi del resoconto: pratiche danno, colli danneggiati, importi */
type ReportFlowKey = 'claims' | 'parcels' | 'amounts';

/** Serie dell'andamento giornaliero (POST /damaged-parcels/graphics) */
type TrendSeriesKey = 'claimsOpened' | 'claimsClosed' | 'damagedParcels';

/** Una voce della lista statistiche di "Totali del periodo" */
interface TotalsStat {
  label: string;
  value: string;
  color?: string;   // pallino colorato (stati)
  icon?: string;    // icona FontAwesome (indicatori)
  title?: string;   // tooltip
}

/** Sezione di "Totali del periodo": un flusso (pratiche, colli o importi) */
interface TotalsSection {
  key: ReportFlowKey;
  label: string;
  icon: string;
  tone: 'blue' | 'green' | 'orange';
  total: number;
  stats: TotalsStat[];
}

/** Tipi di grafico disponibili per l'andamento giornaliero */
type TrendChartType = 'bar' | 'stacked' | 'line' | 'area';

/**
 * /damage-management: pagina di ingresso della gestione danni (entrypoint
 * della funzione "damages" in dashboard), sul modello di /otm-management.
 * Header con titolo, riquadro utente/collegamenti (app-damage-nav-box) e
 * strumentario; sezione "Resoconto" con periodo, totali (pratiche danno,
 * colli danneggiati, importi), andamento giornaliero e torte per stato;
 * sezione "Funzionalità" ancora senza funzionalità. I dati arrivano da
 * POST /damaged-parcels/report (totali) e POST /damaged-parcels/graphics
 * (andamento, una finestra di giorni alla volta).
 */
@Component({
  selector: 'app-damage-management',
  standalone: true,
  imports: [CommonModule, NgChartsModule, DamageNavBoxComponent],
  templateUrl: './damage-management.component.html',
  styleUrls: ['./damage-management.component.css']
})
export class DamageManagementComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  // Stati dei tre flussi, nell'ordine del resoconto, con etichetta e colore (torte e pallini)
  private readonly CLAIM_STATUSES = ['OPEN', 'IN_PROGRESS', 'WAITING', 'CLOSED', 'REJECTED'];
  private readonly PARCEL_STATUSES = ['WITH_OPEN_CLAIM', 'WITH_CLOSED_CLAIM', 'WITHOUT_CLAIM'];
  private readonly AMOUNT_STATUSES = ['PROPOSED', 'APPROVED', 'INVOICED', 'PAID', 'CHARGED', 'CANCELLED'];
  private readonly STATUS_LABELS: { [status: string]: string } = {
    OPEN: 'Aperte',
    IN_PROGRESS: 'In lavorazione',
    WAITING: 'In attesa',
    CLOSED: 'Chiuse',
    REJECTED: 'Respinte',
    WITH_OPEN_CLAIM: 'Con pratica aperta',
    WITH_CLOSED_CLAIM: 'Con pratica chiusa',
    WITHOUT_CLAIM: 'Senza pratica',
    PROPOSED: 'Proposti',
    APPROVED: 'Approvati',
    INVOICED: 'Fatturati',
    PAID: 'Pagati',
    CHARGED: 'Addebitati',
    CANCELLED: 'Annullati'
  };
  private readonly STATUS_COLORS: { [status: string]: string } = {
    OPEN: '#2f74e0',
    IN_PROGRESS: '#e0b300',
    WAITING: '#0a97b5',
    CLOSED: '#2f9e6b',
    REJECTED: '#e0454c',
    WITH_OPEN_CLAIM: '#e0454c',
    WITH_CLOSED_CLAIM: '#2f9e6b',
    WITHOUT_CLAIM: '#8a94a3',
    PROPOSED: '#e0b300',
    APPROVED: '#2f74e0',
    INVOICED: '#0a97b5',
    PAID: '#2f9e6b',
    CHARGED: '#7e3ff2',
    CANCELLED: '#8a94a3'
  };
  private readonly AMOUNT_TYPE_LABELS: { [type: string]: string } = {
    CLAIM: 'Richiesto',
    REFUND: 'Rimborso',
    CHARGE: 'Addebito',
    REPAIR: 'Riparazione',
    PENALTY: 'Penale'
  };

  private readonly STORAGE_KEY = 'damage-management-settings';

  /** Clienti e marchi dell'utente (da userFunctions), inviati per l'autorizzazione */
  clientsBrands: ClientsBrands[] = [];

  // ─── STATO UI ───
  isFeaturesCollapsed = false;
  isReportCollapsed = false;
  isLoadingReport = false;
  reportLoadError = false;

  // ─── SKELETON (caricamento resoconto) ───
  /** Segnaposto: tre sezioni (Pratiche, Colli, Importi) con 8 righe ciascuna */
  readonly skeletonSections = [0, 1, 2];
  readonly skeletonStatRows = Array.from({ length: 8 }, (_, i) => i);
  /** Barre del segnaposto dell'andamento giornaliero (altezze in %) */
  readonly skeletonBars = [45, 70, 55, 85, 60, 30, 25, 65, 80, 50, 72, 40, 35, 68, 58];

  // ─── PERIODO RESOCONTO ───
  reportDateFrom: Date | null = null;
  reportDateTo: Date | null = null;
  private reportDateFromPicker: FlatpickrInstance | null = null;
  private reportDateToPicker: FlatpickrInstance | null = null;

  // I campi data vivono dentro un *ngIf: il picker va agganciato ogni volta che ricompaiono
  @ViewChild('reportDateFromInput') set reportDateFromInput(el: ElementRef<HTMLInputElement> | undefined) {
    this.reportDateFromPicker?.destroy();
    this.reportDateFromPicker = el ? this.createPicker(el.nativeElement, 'from') : null;
  }
  @ViewChild('reportDateToInput') set reportDateToInput(el: ElementRef<HTMLInputElement> | undefined) {
    this.reportDateToPicker?.destroy();
    this.reportDateToPicker = el ? this.createPicker(el.nativeElement, 'to') : null;
  }

  // ─── DATI RESOCONTO ───
  report: DamageReport = this.emptyReport();
  totalsSections: TotalsSection[] = [];
  private reportSubscription: Subscription | null = null;

  // ─── GRAFICI ───
  trendChartData: ChartData<'bar' | 'line'> = { labels: [], datasets: [] };
  // Valorizzato nel costruttore: ng2-charts crea il grafico con le opzioni del primo
  // ngOnChanges e gli update successivi non ridimensionano il canvas, quindi
  // maintainAspectRatio:false deve esserci fin dall'inizio
  trendChartOptions: ChartOptions<'bar' | 'line'>;
  claimsPieData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  parcelsPieData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  amountsPieData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  readonly pieChartPlugins: Plugin<'doughnut'>[] = [ChartDataLabels as unknown as Plugin<'doughnut'>];

  // ─── ANDAMENTO GIORNALIERO: parametri e tipo di grafico ───
  readonly trendSeries: { key: TrendSeriesKey; label: string; color: string }[] = [
    { key: 'claimsOpened', label: 'Pratiche aperte', color: '#2f74e0' },
    { key: 'claimsClosed', label: 'Pratiche chiuse', color: '#2f9e6b' },
    { key: 'damagedParcels', label: 'Colli danneggiati', color: '#e07a25' }
  ];
  trendSeriesVisible: Record<TrendSeriesKey, boolean> = { claimsOpened: true, claimsClosed: true, damagedParcels: true };

  readonly trendChartTypes: { key: TrendChartType; label: string; icon: string }[] = [
    { key: 'bar', label: 'Barre', icon: 'fa-chart-bar' },
    { key: 'stacked', label: 'Barre impilate', icon: 'fa-layer-group' },
    { key: 'line', label: 'Linee', icon: 'fa-chart-line' },
    { key: 'area', label: 'Area', icon: 'fa-chart-area' }
  ];
  trendChartType: TrendChartType = 'bar';

  // ─── ANDAMENTO GIORNALIERO: finestra di visualizzazione (7/15/21/30/60/90 giorni) ───
  // La finestra ha sempre la dimensione scelta e ognuna viene richiesta a POST
  // /damaged-parcels/graphics quando la si visualizza. Senza "Data da" la
  // ricerca è aperta: nessuna data di inizio, si può tornare indietro senza limite.
  readonly trendWindowSizes = [7, 15, 21, 30, 60, 90];
  /** Giorni per finestra (salvato nelle preferenze) */
  trendWindowSize = 30;
  /** Inizio esatto del periodo (Data da), null = ricerca aperta */
  private trendPeriodFrom: Date | null = null;
  /** Fine esatta del periodo (Data a, o fine di oggi) */
  private trendPeriodTo: Date | null = null;
  /** Primo giorno (mezzanotte locale) della finestra visualizzata */
  private trendWindowStartDay: Date | null = null;
  /** Conteggi giornalieri della finestra corrente, allineati a trendWindowDays */
  private trendWindowData: Record<TrendSeriesKey, number[]> = { claimsOpened: [], claimsClosed: [], damagedParcels: [] };
  isLoadingTrend = false;
  trendLoadError = false;
  private trendSubscription: Subscription | null = null;

  /** Primo giorno del periodo (null = ricerca aperta) */
  private get trendFromDay(): Date | null {
    return this.trendPeriodFrom ? this.startOfDay(this.trendPeriodFrom) : null;
  }

  /** Ultimo giorno del periodo */
  private get trendToDay(): Date | null {
    return this.trendPeriodTo ? this.startOfDay(this.trendPeriodTo) : null;
  }

  /** Ultimo giorno della finestra: trendWindowSize giorni dall'inizio, senza superare la fine del periodo */
  private get trendWindowEndDay(): Date | null {
    if (!this.trendWindowStartDay || !this.trendToDay) return null;
    const end = this.addDays(this.trendWindowStartDay, this.trendWindowSize - 1);
    return end > this.trendToDay ? this.trendToDay : end;
  }

  /** Giorni della finestra corrente */
  get trendWindowDays(): Date[] {
    const start = this.trendWindowStartDay;
    const end = this.trendWindowEndDay;
    if (!start || !end) return [];
    const days: Date[] = [];
    for (let day = new Date(start); day <= end; day = this.addDays(day, 1)) {
      days.push(day);
    }
    return days;
  }

  /** Numero di giorni del periodo, solo se ha una data di inizio */
  get trendTotalDays(): number | null {
    if (!this.trendFromDay || !this.trendToDay) return null;
    return Math.round((this.trendToDay.getTime() - this.trendFromDay.getTime()) / 86400000) + 1;
  }

  /** Ricerca aperta (senza Data da) */
  get isTrendPeriodOpen(): boolean {
    return !this.trendPeriodFrom;
  }

  /** Mostra i bottoni < > quando c'è un'altra finestra da visualizzare */
  get hasTrendPaging(): boolean {
    return this.canTrendPrev || this.canTrendNext;
  }

  get canTrendPrev(): boolean {
    if (!this.trendWindowStartDay) return false;
    const fromDay = this.trendFromDay;
    return !fromDay || this.trendWindowStartDay > fromDay;
  }

  get canTrendNext(): boolean {
    const end = this.trendWindowEndDay;
    return !!end && !!this.trendToDay && end < this.trendToDay;
  }

  /** Etichetta "dd/MM – dd/MM" della finestra corrente */
  get trendWindowLabel(): string {
    const start = this.trendWindowStartDay;
    const end = this.trendWindowEndDay;
    if (!start || !end) return '';
    return `${this.formatDayLabel(start)} – ${this.formatDayLabel(end)}`;
  }

  /** Oggi è dentro il periodo del grafico (tra Data da, se c'è, e la fine) */
  get isTodayInTrendPeriod(): boolean {
    const today = this.startOfDay(new Date());
    const fromDay = this.trendFromDay;
    const toDay = this.trendToDay;
    return !!toDay && today <= toDay && (!fromDay || today >= fromDay);
  }

  /** La finestra visualizzata contiene già il giorno attuale */
  get isTodayInTrendWindow(): boolean {
    const start = this.trendWindowStartDay;
    const end = this.trendWindowEndDay;
    const today = this.startOfDay(new Date());
    return !!start && !!end && today >= start && today <= end;
  }

  /**
   * Torna alla finestra che contiene il giorno attuale: oggi è l'ultimo giorno
   * della finestra (senza andare prima della Data da)
   */
  goToCurrentTrendWindow(): void {
    if (!this.isTodayInTrendPeriod || this.isTodayInTrendWindow) return;

    let start = this.addDays(this.startOfDay(new Date()), -(this.trendWindowSize - 1));
    const fromDay = this.trendFromDay;
    if (fromDay && start < fromDay) start = fromDay;
    this.trendWindowStartDay = start;
    this.loadTrendWindow();
  }

  /**
   * Cambia i giorni per finestra: la finestra mantiene lo stesso ultimo
   * giorno (senza andare prima della Data da) e viene richiesta di nuovo
   */
  setTrendWindowSize(size: number): void {
    if (this.trendWindowSize === size) return;
    const endDay = this.trendWindowEndDay;
    this.trendWindowSize = size;
    this.saveUserPreferences();
    if (!endDay) return;

    let start = this.addDays(endDay, -(size - 1));
    const fromDay = this.trendFromDay;
    if (fromDay && start < fromDay) start = fromDay;
    this.trendWindowStartDay = start;
    this.loadTrendWindow();
  }

  /**
   * Sposta la finestra di N giorni indietro (-1) o avanti (+1), senza uscire
   * dal periodo, e richiede al backend i dati della nuova finestra
   */
  shiftTrendWindow(direction: -1 | 1): void {
    if (!this.trendWindowStartDay || !this.trendToDay) return;
    if (direction === -1 && !this.canTrendPrev) return;
    if (direction === 1 && !this.canTrendNext) return;

    let start = this.addDays(this.trendWindowStartDay, direction * this.trendWindowSize);
    // Mai prima della Data da (se c'è) né oltre l'ultima finestra piena
    const lastStart = this.addDays(this.trendToDay, -(this.trendWindowSize - 1));
    if (start > lastStart) start = lastStart;
    const fromDay = this.trendFromDay;
    if (fromDay && start < fromDay) start = fromDay;

    this.trendWindowStartDay = start;
    this.loadTrendWindow();
  }

  /** Tipo Chart.js sottostante: impilate → bar, area → line */
  get trendBaseType(): 'bar' | 'line' {
    return this.trendChartType === 'line' || this.trendChartType === 'area' ? 'line' : 'bar';
  }

  get hasVisibleTrendSeries(): boolean {
    return this.trendSeries.some(s => this.trendSeriesVisible[s.key]);
  }

  /** La finestra corrente ha dati da mostrare (richiesta andata a buon fine) */
  get hasTrendData(): boolean {
    return !!this.trendWindowStartDay && !this.trendLoadError;
  }

  readonly pieChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: { backgroundColor: '#212529', padding: 10, cornerRadius: 8 },
      datalabels: {
        color: '#fff',
        font: { weight: 'bold', size: 11 },
        // Percentuale sullo spicchio (3 decimali), nascosta sotto il 5% per non sovrapporre le etichette
        formatter: (value: number, ctx: any) => {
          const data = ctx.chart.data.datasets[0].data as number[];
          const total = data.reduce((sum, v) => sum + (v || 0), 0);
          if (!total) return '';
          const pct = (value / total) * 100;
          return pct >= 5 ? `${pct.toFixed(3)}%` : '';
        }
      }
    }
  };

  constructor(
    private damageService: DamageManagementService,
    functionService: FunctionService,
    router: Router
  ) {
    super(functionService, router);
    // La funzione dei permessi è "damages" (DamagesController), non il nome della rotta
    this.functionName = 'damages';
    this.trendChartOptions = this.buildTrendChartOptions();
  }

  ngOnInit(): void {
    // Clienti e marchi dell'utente (localStorage), come le altre pagine della gestione danni
    const stored = localStorage.getItem('userFunctions');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.clientsBrands = parsed.clients || [];
      } catch {
        this.clientsBrands = [];
      }
    }

    this.loadUserPreferences();
    // Le date partono vuote: ricerca aperta
    this.loadReport();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeBootstrapTooltips(), 100);
  }

  ngOnDestroy(): void {
    this.hideTooltips();
    this.reportDateFromPicker?.destroy();
    this.reportDateToPicker?.destroy();
    this.reportSubscription?.unsubscribe();
    this.trendSubscription?.unsubscribe();
  }

  // ─── PREFERENZE UTENTE (sezioni collassate, grafico) ───

  private loadUserPreferences(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (!saved) return;
      const settings = JSON.parse(saved);
      if (typeof settings.isFeaturesCollapsed === 'boolean') this.isFeaturesCollapsed = settings.isFeaturesCollapsed;
      if (typeof settings.isReportCollapsed === 'boolean') this.isReportCollapsed = settings.isReportCollapsed;
      if (this.trendChartTypes.some(t => t.key === settings.trendChartType)) this.trendChartType = settings.trendChartType;
      if (this.trendWindowSizes.includes(settings.trendWindowSize)) this.trendWindowSize = settings.trendWindowSize;
      if (settings.trendSeriesVisible && typeof settings.trendSeriesVisible === 'object') {
        this.trendSeries.forEach(s => {
          if (typeof settings.trendSeriesVisible[s.key] === 'boolean') {
            this.trendSeriesVisible[s.key] = settings.trendSeriesVisible[s.key];
          }
        });
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }

  private saveUserPreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        isFeaturesCollapsed: this.isFeaturesCollapsed,
        isReportCollapsed: this.isReportCollapsed,
        trendChartType: this.trendChartType,
        trendWindowSize: this.trendWindowSize,
        trendSeriesVisible: this.trendSeriesVisible
      }));
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  }

  toggleFeaturesCollapsed(): void {
    this.isFeaturesCollapsed = !this.isFeaturesCollapsed;
    this.saveUserPreferences();
  }

  toggleReportCollapsed(): void {
    this.isReportCollapsed = !this.isReportCollapsed;
    this.saveUserPreferences();
  }

  /** True se tutte le sezioni collassabili sono chiuse */
  get areAllSectionsCollapsed(): boolean {
    return this.isFeaturesCollapsed && this.isReportCollapsed;
  }

  /** Comprime o espande tutte le sezioni insieme */
  toggleAllSections(): void {
    const collapse = !this.areAllSectionsCollapsed;
    this.isFeaturesCollapsed = collapse;
    this.isReportCollapsed = collapse;
    this.saveUserPreferences();
  }

  // ─── STRUMENTARIO ───

  /** Aggiorna i dati: ricalcola il resoconto con le date correnti */
  refreshData(): void {
    this.loadReport();
  }

  // ─── PERIODO RESOCONTO ───

  /**
   * Periodo valido: le date sono facoltative; se ci sono entrambe,
   * "a" deve essere dopo "da"
   */
  get isReportPeriodValid(): boolean {
    if (!this.reportDateFrom || !this.reportDateTo) return true;
    return this.reportDateTo > this.reportDateFrom;
  }

  /** Svuota una delle due date del periodo */
  clearReportDate(which: 'from' | 'to'): void {
    if (which === 'from') {
      this.reportDateFromPicker?.clear();
      this.reportDateFrom = null;
    } else {
      this.reportDateToPicker?.clear();
      this.reportDateTo = null;
    }
  }

  /** Riporta il periodo allo stato iniziale: date vuote (ricerca aperta) */
  resetReportPeriod(reload: boolean = true): void {
    this.clearReportDate('from');
    this.clearReportDate('to');

    if (reload) this.loadReport();
  }

  private createPicker(input: HTMLInputElement, which: 'from' | 'to'): FlatpickrInstance {
    const isFrom = which === 'from';
    return flatpickr(input, {
      dateFormat: 'd/m/Y H:i',
      enableTime: true,
      time_24hr: true,
      locale: Italian,
      defaultHour: isFrom ? 0 : 23,
      defaultMinute: isFrom ? 0 : 59,
      defaultDate: (isFrom ? this.reportDateFrom : this.reportDateTo) ?? undefined,
      onChange: (selectedDates: Date[]) => {
        const date = selectedDates[0] ?? null;
        if (isFrom) this.reportDateFrom = date; else this.reportDateTo = date;
      }
    });
  }

  // ─── RESOCONTO (POST /damaged-parcels/report) ───

  /**
   * Calcola il resoconto del periodo: totali di pratiche, colli e importi in
   * una sola richiesta. Le date sono facoltative: se mancano non vengono
   * inviate (nessun filtro). L'andamento giornaliero è una richiesta a parte.
   */
  loadReport(): void {
    if (!this.isReportPeriodValid) return;

    this.reportSubscription?.unsubscribe();
    this.isLoadingReport = true;
    this.reportLoadError = false;

    const dateFrom = this.reportDateFrom ? this.reportDateFrom.toISOString() : null;
    const dateTo = this.reportDateTo ? this.reportDateTo.toISOString() : null;

    this.reportSubscription = this.damageService.getReport(this.clientsBrands, dateFrom, dateTo)
      .pipe(finalize(() => { this.isLoadingReport = false; }))
      .subscribe({
        next: report => {
          this.report = report;
          this.totalsSections = this.buildTotalsSections();
          this.updatePieCharts();
        },
        error: error => {
          console.error('Error loading damage report:', error);
          this.reportLoadError = true;
          this.report = this.emptyReport();
          this.totalsSections = this.buildTotalsSections();
          this.updatePieCharts();
        }
      });

    // Andamento giornaliero: richiesta separata, una finestra alla volta
    this.initTrendPeriod();
    this.loadTrendWindow();
  }

  // ─── ANDAMENTO GIORNALIERO (POST /damaged-parcels/graphics) ───

  /**
   * Prepara il periodo del grafico e parte dall'ultima finestra (trendWindowSize giorni)
   * (i dati più recenti). Senza "Data a" la fine è oggi; senza "Data da" la
   * ricerca resta aperta, senza data di inizio.
   */
  private initTrendPeriod(): void {
    const to = this.reportDateTo ? new Date(this.reportDateTo) : new Date();
    if (!this.reportDateTo) to.setHours(23, 59, 59, 999);

    this.trendPeriodFrom = this.reportDateFrom ? new Date(this.reportDateFrom) : null;
    this.trendPeriodTo = to;

    // Ultima finestra piena, senza andare prima della Data da (se c'è)
    let start = this.addDays(this.startOfDay(to), -(this.trendWindowSize - 1));
    const fromDay = this.trendFromDay;
    if (fromDay && start < fromDay) start = fromDay;
    this.trendWindowStartDay = start;
  }

  /**
   * Richiede i conteggi della finestra corrente (date sempre presenti: sono
   * gli estremi della finestra). Una richiesta ancora in corso (es. click
   * rapidi su < >) viene annullata.
   */
  private loadTrendWindow(): void {
    this.trendSubscription?.unsubscribe();

    const windowDays = this.trendWindowDays;
    // Periodo vuoto (es. Data da nel futuro): nessuna richiesta
    if (!windowDays.length || !this.trendPeriodTo) {
      this.trendWindowData = { claimsOpened: [], claimsClosed: [], damagedParcels: [] };
      this.trendLoadError = true;
      this.updateTrendChart();
      return;
    }

    const firstDay = windowDays[0];
    const lastDay = windowDays[windowDays.length - 1];
    // Estremi della finestra: ai bordi del periodo si usano gli orari esatti del filtro
    const fromDay = this.trendFromDay;
    const from = this.trendPeriodFrom && fromDay && firstDay.getTime() === fromDay.getTime()
      ? new Date(this.trendPeriodFrom)
      : new Date(firstDay);
    let to: Date;
    if (this.trendToDay && lastDay.getTime() === this.trendToDay.getTime()) {
      to = new Date(this.trendPeriodTo);
    } else {
      to = new Date(lastDay);
      to.setHours(23, 59, 59, 999);
    }

    this.isLoadingTrend = true;
    this.trendLoadError = false;

    this.trendSubscription = this.damageService
      .getGraphics(this.clientsBrands, from.toISOString(), to.toISOString())
      .pipe(finalize(() => { this.isLoadingTrend = false; }))
      .subscribe({
        next: (response: DamageGraphicsResponse) => {
          const byDate = new Map((response?.days || []).map(d => [d.date, d]));
          this.trendWindowData = {
            claimsOpened: windowDays.map(day => byDate.get(this.toDateKey(day))?.claimsOpened || 0),
            claimsClosed: windowDays.map(day => byDate.get(this.toDateKey(day))?.claimsClosed || 0),
            damagedParcels: windowDays.map(day => byDate.get(this.toDateKey(day))?.damagedParcels || 0)
          };
          this.updateTrendChart();
        },
        error: (error) => {
          console.error('Error loading damage graphics:', error);
          this.trendLoadError = true;
          this.trendWindowData = { claimsOpened: [], claimsClosed: [], damagedParcels: [] };
          this.updateTrendChart();
        }
      });
  }

  /** Mezzanotte locale del giorno indicato */
  private startOfDay(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  }

  /** Nuova data spostata di n giorni (n negativo = indietro) */
  private addDays(date: Date, n: number): Date {
    const day = new Date(date);
    day.setDate(day.getDate() + n);
    return day;
  }

  /** Chiave yyyy-MM-dd del giorno locale, come le date restituite da /damaged-parcels/graphics */
  private toDateKey(day: Date): string {
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    return `${day.getFullYear()}-${mm}-${dd}`;
  }

  /** Etichetta dd/MM di un giorno */
  private formatDayLabel(day: Date): string {
    return day.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  }

  // ─── TOTALI DEL PERIODO ───

  /** Flusso con tutti gli stati a 0 */
  private emptyFlow(statuses: string[]): DamageReportFlow {
    const byStatus: { [status: string]: number } = {};
    statuses.forEach(s => byStatus[s] = 0);
    return { total: 0, byStatus };
  }

  private emptyReport(): DamageReport {
    return {
      claims: { ...this.emptyFlow(this.CLAIM_STATUSES), overdue: 0, assigned: 0, closedInPeriod: 0 },
      parcels: { ...this.emptyFlow(this.PARCEL_STATUSES), withImages: 0, damageEvents: 0 },
      amounts: { ...this.emptyFlow(this.AMOUNT_STATUSES), totalValue: 0, byType: {}, valueByStatus: {} }
    };
  }

  /** Conteggi per stato nell'ordine del resoconto (stati sconosciuti in coda) */
  private orderedStatuses(flow: DamageReportFlow, statuses: string[]): string[] {
    const extra = Object.keys(flow.byStatus || {}).filter(s => !statuses.includes(s));
    return [...statuses, ...extra];
  }

  statusLabel(status: string): string {
    return this.STATUS_LABELS[status] || status.replace(/_/g, ' ');
  }

  private amountTypeLabel(type: string): string {
    return this.AMOUNT_TYPE_LABELS[type] || type.replace(/_/g, ' ');
  }

  private formatCurrency(value: number): string {
    return (value || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
  }

  /**
   * Costruisce le tre sezioni di "Totali del periodo": per ciascun flusso il
   * totale e la lista di statistiche (conteggio per stato + indicatori).
   */
  private buildTotalsSections(): TotalsSection[] {
    const fmt = (n: number) => (n || 0).toLocaleString('it-IT');
    const pct = (n: number, total: number, digits = 3) => `${(total ? (n / total) * 100 : 0).toFixed(digits)}%`;

    const statusStats = (flow: DamageReportFlow, statuses: string[], unit: string): TotalsStat[] =>
      this.orderedStatuses(flow, statuses).map(status => {
        const count = flow.byStatus[status] || 0;
        return {
          label: this.statusLabel(status),
          value: fmt(count),
          color: this.STATUS_COLORS[status] || '#adb5bd',
          title: `${this.statusLabel(status)}: ${pct(count, flow.total)} ${unit}`
        };
      });

    const claims = this.report.claims;
    const claimsSection: TotalsSection = {
      key: 'claims', label: 'Pratiche danno', icon: 'fa-folder-open', tone: 'blue', total: claims.total,
      stats: [
        ...statusStats(claims, this.CLAIM_STATUSES, 'delle pratiche'),
        { label: 'Scadute', value: fmt(claims.overdue), icon: 'fa-hourglass-end', title: 'Pratiche aperte con scadenza superata' },
        { label: 'Assegnate', value: pct(claims.assigned, claims.total), icon: 'fa-user-check', title: `${fmt(claims.assigned)} pratiche con un operatore assegnato` },
        { label: 'Chiuse nel periodo', value: fmt(claims.closedInPeriod), icon: 'fa-flag-checkered', title: 'Pratiche chiuse o respinte nel periodo (per data di chiusura)' }
      ]
    };

    const parcels = this.report.parcels;
    const parcelsSection: TotalsSection = {
      key: 'parcels', label: 'Colli danneggiati', icon: 'fa-hammer', tone: 'orange', total: parcels.total,
      stats: [
        ...statusStats(parcels, this.PARCEL_STATUSES, 'dei colli'),
        { label: 'Con immagini', value: pct(parcels.withImages, parcels.total), icon: 'fa-images', title: `${fmt(parcels.withImages)} colli con almeno una foto del danno` },
        { label: 'Eventi di danno', value: fmt(parcels.damageEvents), icon: 'fa-exclamation-triangle', title: 'Eventi DAMAGE registrati sui colli nel periodo' }
      ]
    };

    const amounts = this.report.amounts;
    const typeStats: TotalsStat[] = Object.keys(amounts.byType || {}).sort().map(type => ({
      label: this.amountTypeLabel(type),
      value: this.formatCurrency(amounts.byType[type]),
      icon: 'fa-tag',
      title: `Somma degli importi di tipo ${type} (esclusi gli annullati)`
    }));
    const amountsSection: TotalsSection = {
      key: 'amounts', label: 'Importi', icon: 'fa-euro-sign', tone: 'green', total: amounts.total,
      stats: [
        ...statusStats(amounts, this.AMOUNT_STATUSES, 'degli importi'),
        { label: 'Totale importi', value: this.formatCurrency(amounts.totalValue), icon: 'fa-coins', title: 'Somma degli importi del periodo, esclusi gli annullati' },
        ...typeStats
      ]
    };

    return [claimsSection, parcelsSection, amountsSection];
  }

  // ─── ANDAMENTO GIORNALIERO: controlli ───

  /** Mostra/nasconde una serie nel grafico */
  toggleTrendSeries(key: TrendSeriesKey): void {
    this.trendSeriesVisible[key] = !this.trendSeriesVisible[key];
    this.saveUserPreferences();
    this.updateTrendChart();
  }

  /** Cambia il tipo di grafico dell'andamento giornaliero */
  setTrendChartType(type: TrendChartType): void {
    if (this.trendChartType === type) return;
    this.trendChartType = type;
    this.saveUserPreferences();
    this.updateTrendChart();
  }

  // ─── GRAFICI ───

  /** Torte per stato: dai Totali del periodo */
  private updatePieCharts(): void {
    this.claimsPieData = this.buildPieData(this.report.claims, this.CLAIM_STATUSES);
    this.parcelsPieData = this.buildPieData(this.report.parcels, this.PARCEL_STATUSES);
    this.amountsPieData = this.buildPieData(this.report.amounts, this.AMOUNT_STATUSES);
  }

  /**
   * Ricostruisce dati e opzioni dell'andamento giornaliero in base alle
   * serie visibili e al tipo di grafico scelto. Sono sempre oggetti
   * nuovi: ng2-charts aggiorna il grafico solo se cambia il riferimento.
   */
  private updateTrendChart(): void {
    const isLine = this.trendBaseType === 'line';
    const isArea = this.trendChartType === 'area';
    const labels = this.trendWindowDays.map(day => this.formatDayLabel(day));

    const datasets = this.trendSeries
      .filter(s => this.trendSeriesVisible[s.key])
      .map(s => {
        const data = this.trendWindowData[s.key];
        if (isLine) {
          return {
            label: s.label,
            data,
            borderColor: s.color,
            backgroundColor: isArea ? this.withAlpha(s.color, 0.18) : s.color,
            pointBackgroundColor: s.color,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.35,
            fill: isArea
          };
        }
        return { label: s.label, data, backgroundColor: s.color, borderRadius: 4 };
      });

    this.trendChartData = { labels, datasets };
    this.trendChartOptions = this.buildTrendChartOptions();
  }

  private buildTrendChartOptions(): ChartOptions<'bar' | 'line'> {
    const stacked = this.trendChartType === 'stacked';
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: { backgroundColor: '#212529', padding: 10, cornerRadius: 8 }
      },
      scales: {
        x: { stacked, grid: { display: false }, ticks: { color: '#6c757d', font: { size: 11 } } },
        y: { stacked, beginAtZero: true, grid: { color: '#f1f3f5' }, ticks: { color: '#6c757d', font: { size: 11 }, precision: 0 } }
      }
    };
  }

  /** Colore esadecimale #rrggbb → rgba con l'alpha indicato */
  private withAlpha(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private buildPieData(flow: DamageReportFlow, statuses: string[]): ChartData<'doughnut'> {
    const keys = this.orderedStatuses(flow, statuses);
    return {
      labels: keys.map(s => this.statusLabel(s)),
      datasets: [{
        data: keys.map(s => flow.byStatus[s] || 0),
        backgroundColor: keys.map(s => this.STATUS_COLORS[s] || '#adb5bd'),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 6
      }]
    };
  }

  // ─── TOOLTIP BOOTSTRAP ───

  /**
   * Aggancia i tooltip di Bootstrap ai bottoni dello strumentario. Gli
   * attributi `data-bs-toggle` da soli non bastano: senza questa
   * inizializzazione resta il tooltip nativo del browser.
   */
  private initializeBootstrapTooltips(): void {
    const bs = (window as any)?.bootstrap;
    if (typeof document === 'undefined' || !bs?.Tooltip) { return; }

    const triggers = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    triggers.forEach(el => {
      try {
        // Senza questo controllo ogni chiamata creerebbe un'istanza in più
        if (bs.Tooltip.getInstance(el)) { return; }
        bs.Tooltip.getOrCreateInstance(el, {
          container: 'body',   // fuori dallo strumentario: niente clipping
          trigger: 'hover',    // senza focus il tooltip resta aperto dopo il click
          placement: 'bottom'
        });
      } catch (error) {
        console.error('Error initializing tooltip:', error);
      }
    });
  }

  /** Chiude i tooltip aperti (uscendo dalla pagina resterebbero a video) */
  private hideTooltips(): void {
    if (typeof document !== 'undefined' && typeof window !== 'undefined' && (window as any).bootstrap) {
      const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"], [data-toggle="tooltip"]');
      tooltipElements.forEach(element => {
        try {
          const tooltipInstance = (window as any).bootstrap.Tooltip.getInstance(element);
          if (tooltipInstance) {
            tooltipInstance.hide();
          }
        } catch (error) {
          console.error('Error hiding tooltip:', error);
        }
      });
    }
  }
}
